// SHCT-582 — PRD 21 v2.14 §7.4 **P-2** 「쓰기 UI 가드」, supplied from one place.
//
// §7.4 fixes P-2 as: `READ` 보유 · `WRITE`(또는 `DELETE`) 미보유 → 버튼 `disabled` + 툴팁
// (`현재 Role로는 이 작업을 수행할 수 없습니다.`), 후속 동작 없음. Two halves, and until this hook
// existed each screen supplied them separately: some buttons carried the tooltip, some only the
// `disabled`, and several screens hid the action outright — which §7.4's 구현 시 유의 rules out,
// because an operator who cannot see the button cannot learn that a permission is what stops them.
//
// `denyProps` also stamps the marker the L2 runtime guard looks for, so the P-2 and P-3 halves of
// the rule can never be applied to different sets of buttons — see permission-runtime-guard.ts.
//
// Root short-circuits to WRITE inside `useCan`, so nothing here needs to special-case it.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useCan } from '@/features/roles/hooks/use-can';
import type { MenuCategoryKey } from '@/features/roles/types/role.types';

/** Marker attribute the capture-phase L2 interceptor matches on. */
export const PERM_DENIED_ATTR = 'data-perm-denied';

/**
 * Props to spread onto a control the current Role may not operate.
 * Empty when the action is permitted, so the same spread is safe on every render.
 */
export type WriteDenyProps = Record<string, never> | { title: string; [PERM_DENIED_ATTR]: 'true' };

const ALLOWED: WriteDenyProps = {};

export interface WriteGuard {
  /** True when the current Role holds WRITE on the category. */
  canWrite: boolean;
  /** The §7.4 P-2 copy, or undefined while the action is permitted. */
  tooltip: string | undefined;
  /** Spread onto the guarded control, alongside `disabled={!canWrite || …}`. */
  denyProps: WriteDenyProps;
}

/**
 * P-2 for a control whose permitted/denied state is already known — typically because the parent
 * resolved the category once and passes `canWrite` down as a prop.
 */
export function useWriteDenyGuard(canWrite: boolean): WriteGuard {
  const { t } = useTranslation('common');
  const tooltip = canWrite ? undefined : t('gate.noPermission.action');
  const denyProps = useMemo<WriteDenyProps>(
    () => (canWrite || !tooltip ? ALLOWED : { title: tooltip, [PERM_DENIED_ATTR]: 'true' }),
    [canWrite, tooltip],
  );
  return { canWrite, tooltip, denyProps };
}

/** P-2 for a control gated by a menu category directly. */
export function useWriteGuard(category: MenuCategoryKey): WriteGuard {
  return useWriteDenyGuard(useCan(category, 'WRITE'));
}
